import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Dropdown from '../Dropdown/Dropdown';
import SelectOption from '../SelectOption/SelectOption';

import ArrowDropdown24 from '../../icons/ArrowDropdown24';

import classNames from '../../utils/classNames';

import styles from './select.scss';

export default function Select(props) {
    const { defaultValue } = props;
    const [value, setValue] = useState(defaultValue);
    const [currentIndex, setCurrentIndex] = useState(undefined);
    const [focused, setFocused] = useState(false);
    let dropdownRef = null;

    /**
     * Find selected value index.
     * @param {number} value Current value.
     * @returns {number} Selected value index.
     */
    function findSelectedIndex(value) {
        return props.options.findIndex((item) => {
            value = typeof item.value === 'number' ? Number(value) : value;

            return item.value === value;
        });
    }

    /**
     * Get label of selected value using current Index.
     * @returns {string} Selected value label.
     */
    function getSelectedLabel() {
        const selected = props.options[currentIndex];

        return selected ? selected.label : undefined;
    }

    /**
     * Opens Dropdown menu on Select wrapper click.
     * @param {event} e Event triggered.
     */
    function handleClick(e) {
        if (props.disabled) return;
        setFocused(!focused);
    }

    /**
     * Handles click on option from dropdown menu.
     * @param {object} option Clicked option.
     */
    function handleOptionClick(option) {
        setValue(option.value);
        setFocused(false);
    }

    /**
     *
     * @param {event} e Event triggered on option MouseDown.
     */
    function handleOptionDown(e) {
        e.preventDefault();
    }

    /**
     * Updates current value index.
     */
    useEffect(() => {
        setCurrentIndex(findSelectedIndex(value));

        if (typeof props.onChange === 'function') {
            props.onChange(value);
        }
    }, [value]);

    /**
     * Adding MouseDown listener, when Dropdown menu opens.
     * If user clicks outside of the menu, we will close it.
     */
    useEffect(() => {
        if (focused) {
            window.addEventListener('mousedown', handleOutsideDown);
        }

        return () => {
            window.removeEventListener('mousedown', handleOutsideDown);
        };
    }, [focused, handleOutsideDown]);

    useEffect(() => {
        setValue(defaultValue);
    }, [defaultValue]);

    /**
     * Handling outside of dropdown menu MouseDown event.
     * Close menu if clicked outside.
     * @param {event} e triggered event
     */
    function handleOutsideDown(e) {
        const popouts = dropdownRef.current.closest('.panel-in').getElementsByClassName('popouts-portal')[0];

        if (popouts) {
            const isMenuClicked = popouts.contains(e.target) || dropdownRef.current.contains(e.target);

            if (focused && !isMenuClicked) {
                setFocused(false);
            }
        }
    }

    /** Render options via SelectOption items. */
    function renderSelectOptions(options) {
        return options.map((item, i) => {
            return (
                <SelectOption
                    key={item.value}
                    onMouseDown={handleOptionDown}
                    onClick={() => {
                        handleOptionClick(item);
                    }}
                    label={item.label}
                    disabled={item.disabled}
                    selected={value === item.value}
                />
            );
        });
    }

    const wrapperStyles = classNames(styles.wrapper, {
        [styles.focused]: focused,
        [styles.disabled]: props.disabled,
    });

    return (
        <Dropdown
            options={props.options}
            renderOptions={renderSelectOptions}
            shown={focused}
            getRootRef={(ref) => {
                dropdownRef = ref;
            }}
        >
            <div className={styles.presenation}>
                <div className={wrapperStyles} onClick={handleClick}>
                    <div className={styles.content}>
                        {value !== undefined ? (
                            <span className={styles.value}>{getSelectedLabel()}</span>
                        ) : (
                            <span className={styles.placeholder}>{props.placeholder}</span>
                        )}
                    </div>
                    <div className={styles.chevron}>
                        <ArrowDropdown24 />
                    </div>
                </div>
            </div>
        </Dropdown>
    );
}

Select.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        })
    ).isRequired,
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    placeholder: PropTypes.string,
    /**
     * Disabled select field.
     */
    disabled: PropTypes.bool,
    /**
     * Callback on select value changed.
     *
     * @return {string} selected value
     */
    onChange: PropTypes.func,
};

Select.defaultProps = {
    placeholder: 'Выберите',
};
